import React, { useEffect, useState } from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Chip,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridColDef, GridRowsProp } from '@mui/x-data-grid';
import { APIEndpoint } from 'common/enum';
import {
  universalGetRequest,
  universalPutRequest,
} from 'common/requestHandler';
import HeaderComp from 'components/elements/HeadingComponent';

// Define the type for the user data
interface User {
  username: string;
  status: string;
  accountStatus: string;
  name: string;
  profile: string;
  userEmail: string;
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [pageSize, setPageSize] = useState<number>(5);
  const [showModal, setShowModal] = useState(false);
  const [fieldValue, setFieldValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch active users
  const fetchActiveUsers = async () => {
    setLoading(true);
    try {
      const response: any = await universalGetRequest(
        APIEndpoint.getSecDocsMain,
        {
          httpMethod: 'GET',
          actionName: 'GetActiveUsers',
        }
      );
      setUsers(response.data); // Assuming the response contains a 'data' field
    } catch (error) {
      console.error('Error fetching active users:', error);
    } finally {
      setLoading(false);
    }
  };

  // Disable selected users
  const disableSelectedUsers = async () => {
    setLoading(true);
    try {
      await universalPutRequest(APIEndpoint.secDocsMain, {
        actionName: 'DisableUsers',
        usernames: selectedUsers,
        httpMethod: 'PUT',
      });
      fetchActiveUsers(); // Refresh the user list after disabling
    } catch (error) {
      console.error('Error disabling users:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchActiveUsers();
  }, []);

  // Copy email to clipboard function
  const copyToClipboard = async (email: string) => {
    try {
      await navigator.clipboard.writeText(email);
      // You could add a toast notification here if you have one
    } catch (err) {
      console.error('Failed to copy email:', err);
    }
  };

  // Filter users based on search term
  const filteredUsers = users.filter((user) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (user.username?.toLowerCase() || '').includes(searchLower) ||
      (user.name?.toLowerCase() || '').includes(searchLower) ||
      (user.userEmail?.toLowerCase() || '').includes(searchLower) ||
      (user.profile?.toLowerCase() || '').includes(searchLower) ||
      (user.accountStatus?.toLowerCase() || '').includes(searchLower)
    );
  });

  // Define columns for DataGrid
  const columns: GridColDef[] = [
    {
      field: 'checkbox',
      headerName: '',
      width: 50,
      renderCell: (params) => (
        <input
          type="checkbox"
          checked={selectedUsers.includes(params.row.username)}
          onChange={() => toggleUserSelection(params.row.username)}
          style={{
            outline: 'none', // Removes blue border
            cursor: 'pointer', // Adds a pointer cursor
          }}
        />
        // <Checkbox size="small" />
      ),
    },
    {
      field: 'username',
      headerName: 'Username',
      width: 200,
      renderCell: (params) => (
        <Tooltip
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>{params.row.userEmail}</span>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  copyToClipboard(params.row.userEmail);
                }}
                sx={{
                  color: 'white',
                  p: 0.5,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>
          }
          arrow
          placement="top"
        >
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
      renderCell: (params) => (
        <Tooltip
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>{params.row.userEmail}</span>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  copyToClipboard(params.row.userEmail);
                }}
                sx={{
                  color: 'white',
                  p: 0.5,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>
          }
          arrow
          placement="top"
        >
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    { field: 'profile', headerName: 'Profile', width: 180 },
    {
      field: 'accountStatus',
      headerName: 'Account Status',
      width: 160,
      align: 'center',
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value === 'Active' ? 'success' : 'error'}
          variant="filled"
        />
      ),
    },
  ];

  // Toggle selection of user
  const toggleUserSelection = (username: string) => {
    setSelectedUsers((prevSelected) =>
      prevSelected.includes(username)
        ? prevSelected.filter((user) => user !== username)
        : [...prevSelected, username]
    );
  };

  return (
    <>
      <HeaderComp>User management</HeaderComp>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          gap: 2,
        }}
      >
        <Button
          variant="contained"
          onClick={disableSelectedUsers}
          disabled={selectedUsers.length === 0}
          size="small"
          startIcon={<PersonOffIcon />}
          sx={{
            bgcolor: '#ef4444',
            color: 'white',
            fontWeight: 600,
            px: 3,
            py: 1,
            borderRadius: 2,
            textTransform: 'none',
            '&:hover': {
              bgcolor: '#dc2626',
            },
            '&:disabled': {
              bgcolor: '#fca5a5',
              color: '#ffffff',
            },
          }}
        >
          Disable Selected Users
        </Button>

        {/* Search Input */}
        <Box sx={{ position: 'relative', maxWidth: '400px', flex: 1 }}>
          <TextField
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: '#6b7280', mr: 1 }} />,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '& fieldset': {
                  borderColor: '#e2e8f0',
                },
                '&:hover fieldset': {
                  borderColor: '#3b82f6',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#3b82f6',
                },
              },
            }}
          />
        </Box>
      </Box>

      <div style={{ height: 400, width: '100%' }}>
        <DataGrid
          rows={
            filteredUsers.map((user) => ({
              ...user,
              id: user.username,
            })) as GridRowsProp
          } // Map filtered users to add `id`
          columns={columns}
          pagination
          loading={loading}
          checkboxSelection={false} // Disable default checkbox column
        />
      </div>
    </>
  );
};
export default UserManagement;
